
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to build a backend-only Plaid integration by using Hosted Link for your frontend'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Image = makeShortcode("Image");
const Callout = makeShortcode("Callout");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Hosted Link' subtitle='Integrate with Link using a Plaid-hosted frontend' mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`With Hosted Link, Plaid hosts the Link experience. Customers can use this link in web browsers or open it in a secure web context within a mobile app, eliminating the need for front-end implementation work. `}</p>
    <p>{`Hosted Link is the recommended Link mode when the official Plaid mobile or web SDKs cannot be used. This includes webview-based mobile apps, or integrations that do not fully own their frontend experience, such as `}<a parentName="p" {...{
        "href": "/docs/link/hosted-link/embedded-clients/"
      }}>{`embedded payment service providers`}</a>{`. Hosted Link can also be used for applications where you do not have a frontend at all; for example, you can send a Hosted Link session to customers who are in-person at a retail location.`}</p>
    <Image src='/assets/img/docs/hosted-link-series.png' alt='image of Hosted Link flow' expandable='true' caption='Example Hosted Link flow: you send a link to your customer and they complete the Plaid-hosted Link flow. You do no front-end integration work.' mdxType="Image" />
    <p>{`With Link Delivery (beta), Hosted Link can also deliver the URL for the Link session to your users via text or email. `}</p>
    <Callout mdxType="Callout">
      <p>{`To request access to Link Delivery, contact your account manager.`}</p>
    </Callout>
    <p>{`Hosted Link is fully supported with all Plaid products and Link flows (including `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`update mode`}</a>{`), except for Layer and Identity Verification, though Identity Verification customers can optionally use Hosted Link with some limitations, as described below. `}</p>
    <Callout mdxType="Callout">
      <p>{`For Identity Verification customers, Hosted Link can optionally be used instead of IDV-specific `}<a parentName="p" {...{
          "href": "/docs/identity-verification/#verification-links-hosted-flow"
        }}>{`Verification Links`}</a>{` to take advantage of features such as Link Delivery (beta); however, when using Hosted Link with Identity Verification, the session will not redirect to the `}<inlineCode parentName="p">{`completion_redirect_uri`}</inlineCode>{`.`}</p>
    </Callout>
    <h5 {...{
      "id": "who-should-use-hosted-link"
    }}>{`Who should use Hosted Link`}</h5>
    <p>{`Hosted Link is recommended when any of the following apply:`}</p>
    <ul>
      <li parentName="ul">{`You are using Plaid within a webview-based mobile app. `}</li>
      <li parentName="ul">{`You do not have an application frontend.`}</li>
      <li parentName="ul">{`Your users are launching Link from outside an in-app context (e.g. by scanning a QR code or receiving an SMS triggered by a customer service representative).`}</li>
      <li parentName="ul">{`You are launching Link from inside an iFrame or an embedded client experience where you do not control the frontend (e.g. PSP integrations).`}</li>
    </ul>
    <Callout mdxType="Callout">
      <p>{`For details on using Hosted Link as an embedded client, see `}<a parentName="p" {...{
          "href": "/docs/link/hosted-link/embedded-clients"
        }}>{`Hosted Link for embedded clients`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "integration-process"
    }}>{`Integration process`}</h4>
    <YouTubeEmbed videoId='7wETps5G04A' alignment='full' title='Hosted Link in 3 Minutes' caption='Prefer to learn by watching? Check out this quick video guide to enabling Hosted Link!' mdxType="YouTubeEmbed" />
    <h5 {...{
      "id": "creating-a-link-token"
    }}>{`Creating a Link token`}</h5>
    <p>{`To start with Hosted Link, call `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` and include a `}<inlineCode parentName="p">{`hosted_link`}</inlineCode>{` object in the request. The `}<inlineCode parentName="p">{`hosted_link`}</inlineCode>{` object can be an empty object `}<inlineCode parentName="p">{`{}`}</inlineCode>{`, or it can contain any of the `}<inlineCode parentName="p">{`hosted_link`}</inlineCode>{` configuration fields. As long as you include the `}<inlineCode parentName="p">{`hosted_link`}</inlineCode>{` object in your request, the `}<inlineCode parentName="p">{`hosted_link_url`}</inlineCode>{` will be present in the response. `}</p>
    <p>{`Previously, Hosted Link was enabled via Plaid account managers; it is no longer necessary to request that your account manager enable Hosted Link. If your account was enabled for Hosted Link via your account manager, the `}<inlineCode parentName="p">{`hosted_link_url`}</inlineCode>{` will be present in the response for all `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` requests, regardless of whether you include a `}<inlineCode parentName="p">{`hosted_link`}</inlineCode>{` object.`}</p>
    <p>{`You can provide the following Hosted Link parameters to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`:`}</p>
    <ul>
      <li parentName="ul">{`Specify a `}<inlineCode parentName="li">{`webhook`}</inlineCode>{` endpoint where the `}<inlineCode parentName="li">{`public_token`}</inlineCode>{` will be delivered. Webhooks are strongly recommended when using Hosted Link, as the webhook is the primary means of delivering the `}<inlineCode parentName="li">{`public_token`}</inlineCode>{`, as well as of informing you when your user has completed the Link session.`}</li>
      <li parentName="ul">{`To have Plaid deliver the link to your user with Link Delivery (beta), set `}<inlineCode parentName="li">{`hosted_link.delivery_method`}</inlineCode>{` to either `}<inlineCode parentName="li">{`sms`}</inlineCode>{` or `}<inlineCode parentName="li">{`email`}</inlineCode>{`. Plaid will contact the user via the information you provide in the `}<inlineCode parentName="li">{`user`}</inlineCode>{` object of the request. For example, if you selected `}<inlineCode parentName="li">{`email`}</inlineCode>{`, the `}<inlineCode parentName="li">{`user`}</inlineCode>{` object in the request must contain an `}<inlineCode parentName="li">{`email_address`}</inlineCode>{`. `}</li>
      <li parentName="ul">{`To customize the duration of the Hosted Link token lifetime, set `}<inlineCode parentName="li">{`hosted_link.url_lifetime_seconds`}</inlineCode>{`. If you don't set this field, the default lifetime for Hosted Links sent by Plaid via SMS is 24 hours, and for links sent via email, it is 7 days. If Plaid isn't delivering the URL, the default lifetime is 30 minutes.`}</li>
      <li parentName="ul">{`To have Plaid redirect the user after they complete the Link flow, set `}<inlineCode parentName="li">{`hosted_link.completion_redirect_uri`}</inlineCode>{` to the destination URI.`}</li>
      <li parentName="ul">{`To indicate that the user will be opening the link in a mobile app in an `}<inlineCode parentName="li">{`ASWebAuthenticationSession`}</inlineCode>{` or Android Custom Tab, set `}<inlineCode parentName="li">{`hosted_link.is_mobile_app`}</inlineCode>{` to `}<inlineCode parentName="li">{`true`}</inlineCode>{`.`}</li>
    </ul>
    <p>{`The response from `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` will include a `}<inlineCode parentName="p">{`hosted_link_url`}</inlineCode>{` field. You can send a user to this URL to start the Hosted Link session. Alternatively, if your integration uses both Hosted Link and non-Hosted Link sessions, you can start a non-Hosted Link session using the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` instead.`}</p>
    <p>{`In a Hosted Link session, you should also store the `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` value that gets returned, and make sure to associate it with the internal user ID your application is using for this user. This is the only way to make sure the data that gets returned from the Hosted Link session is associated with the right end-user.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Sample /link/token/create request for Hosted Link",
        "switcher": "false",
        "#Sample": true,
        "/link/token/create": true,
        "request": true,
        "for": true,
        "Hosted": true,
        "Link": true
      }}>{`
curl -X POST https://production.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
    "client_name": "Wonderwallet",
    "country_codes": ["US"],
    "redirect_uri": "{{UNIVERSAL_OR_APP_LINK}}",
    "webhook": "https://wonderwallet.com/webhook_receiver",
    "language": "en",
    "user": {
      "client_user_id": "a57d3304",
      "phone_number": "+19162255887"
    },
  "products": ["auth"],
  "hosted_link": {
    "delivery_method": "sms",
    "completion_redirect_uri": "https://wonderwallet.com/redirect",
    "is_mobile_app": false,
    "url_lifetime_seconds": 900
  }
}'
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Sample /link/token/create response",
        "switcher": "false",
        "#Sample": true,
        "/link/token/create": true,
        "response": true
      }}>{`{
  "expiration": "2023-08-18T02:08:03Z",
  "hosted_link_url": "https://secure.plaid.com/hl/lp9o97618r1r6p93oon62nr025950ro4s3",
  "link_token": "link-production-4b42163e-6e1c-48bb-a17a-e570405eb9f8",
  "request_id": "kNbPMmLxzBObzpt"
}
`}</code></pre>
    <h5 {...{
      "id": "obtaining-a-public-token"
    }}>{`Obtaining a public token`}</h5>
    <p>{`In most other Plaid integration methods, upon completion of the Link flow, your frontend code would receive either an `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` or `}<inlineCode parentName="p">{`onExit`}</inlineCode>{` callback. In Hosted Link, there is no frontend integration required (or possible). You will instead receive information about the Link flow (including the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{`) via the `}<a parentName="p" {...{
        "href": "/docs/api/link/#session_finished"
      }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a>{` webhook and the `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` endpoint.`}</p>
    <p>{`Each time a user completes a Link flow, Plaid will fire a `}<a parentName="p" {...{
        "href": "/docs/api/link/#session_finished"
      }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a>{` webhook that contains information about what caused the session to end, as well as the public token if the session completed successfully. In flows where Link is re-initialized after an OAuth redirect, this webhook will fire exactly once, after the user completes the entire Link flow.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Sample SESSION_FINISHED webhook",
        "switcher": "false",
        "#Sample": true,
        "SESSION_FINISHED": true,
        "webhook": true
      }}>{`{
  "webhook_type": "LINK",
  "webhook_code": "SESSION_FINISHED",
  "status": "success",
  "link_session_id": "356dbb28-7f98-44d1-8e6d-0cec580f3171",
  "link_token": "link-sandbox-af1a0311-da53-4636-b754-dd15cc058176",
  "public_tokens": [
    "public-sandbox-b0e2c4ee-a763-4df5-bfe9-46a46bce993d"
  ],
  "environment": "sandbox"
}
`}</code></pre>
    <p>{`You can also call `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` with the relevant Link token to receive more detailed metadata on sessions started using this Link token. Each Link session includes the time it was started, and (if applicable) the time it finished. Each completed Link session also includes the metadata that would normally be passed to the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` or `}<inlineCode parentName="p">{`onExit`}</inlineCode>{` callbacks, including the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` for successful sessions.`}</p>
    <p>{`Detailed session data, including the public token, will also be available from `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` for six hours after the session has completed.  In general, it is recommended to use the webhook to obtain the public token, since it will allow you to get the public token more promptly, but you may want to use `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` if your integration does not use webhooks, or as a backup mechanism if your system missed webhooks due to an outage. `}</p>
    <p>{`In `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` is present in both the `}<inlineCode parentName="p">{`on_success`}</inlineCode>{` object and the `}<inlineCode parentName="p">{`results`}</inlineCode>{` object. It is recommended to use the `}<inlineCode parentName="p">{`results`}</inlineCode>{` object, as the older `}<inlineCode parentName="p">{`on_success`}</inlineCode>{` object does not support the newer `}<a parentName="p" {...{
        "href": "/docs/link/multi-item-link"
      }}>{`Multi-Item Link`}</a>{` option, which allows for multiple public tokens to be returned in a single Link session.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Sample /link/token/get response",
        "switcher": "false",
        "#Sample": true,
        "/link/token/get": true,
        "response": true
      }}>{`{
  "created_at": "2024-07-29T21:22:13Z",
    "expiration": "2024-07-29T21:52:13Z",
    "link_sessions": [
        {
            "events": [
                {
                    "event_id": "c1ccb962-9de0-494c-b85b-e89cdf3b2478",
                    "event_metadata": {
                        "institution_id": "ins_10",
                        "institution_name": "American Express",
                        "request_id": "Z9MB4fuMB2TJqgL"
                    },
                    "event_name": "HANDOFF",
                    "timestamp": "2024-07-29T21:23:03Z"
                },
                {
                    "event_id": "ff03f1ff-313b-4c2e-997f-83791c7ec432",
                    "event_metadata": {
                        "institution_id": "ins_10",
                        "institution_name": "American Express",
                        "request_id": "vyPamzbGPPsQucO"
                    },
                    "event_name": "OPEN_OAUTH",
                    "timestamp": "2024-07-29T21:22:34Z"
                },
                {
                    "event_id": "0ea6d0df-1bb0-4e4d-bea5-b3731b7536bf",
                    "event_metadata": {
                        "institution_id": "ins_10",
                        "institution_name": "American Express",
                        "request_id": "vyPamzbGPPsQucO"
                    },
                    "event_name": "TRANSITION_VIEW",
                    "timestamp": "2024-07-29T21:22:33Z"
                },
                {
                    "event_id": "e14c7b49-82c4-4f0c-a88a-987a62241d34",
                    "event_metadata": {
                        "institution_id": "ins_10",
                        "institution_name": "American Express",
                        "request_id": "0jjsLDlMl1VaVxU"
                    },
                    "event_name": "SELECT_INSTITUTION",
                    "timestamp": "2024-07-29T21:22:32Z"
                },
                {
                    "event_id": "b9b9f7ce-52bc-470e-801b-b4fa1da98e59",
                    "event_metadata": {
                        "request_id": "0jjsLDlMl1VaVxU"
                    },
                    "event_name": "TRANSITION_VIEW",
                    "timestamp": "2024-07-29T21:22:23Z"
                },
                {
                    "event_id": "e057a92c-eff8-467c-92c2-620109207e87",
                    "event_metadata": {
                        "request_id": "aHnAMiUzk8HrsNn"
                    },
                    "event_name": "SKIP_SUBMIT_PHONE",
                    "timestamp": "2024-07-29T21:22:22Z"
                },
                {
                    "event_id": "f1feedc9-61be-4a42-885c-cb67511473d2",
                    "event_metadata": {
                        "request_id": "aHnAMiUzk8HrsNn"
                    },
                    "event_name": "TRANSITION_VIEW",
                    "timestamp": "2024-07-29T21:22:21Z"
                },
                {
                    "event_id": "f9b49b0b-4259-4a32-860b-653b55ff8736",
                    "event_metadata": {
                        "request_id": "CGTlWrGRd8WaGa2"
                    },
                    "event_name": "TRANSITION_VIEW",
                    "timestamp": "2024-07-29T21:22:20Z"
                }
            ],
            "finished_at": "2024-07-29T21:23:03.326860787Z",
            "link_session_id": "f3c3b8fe-16c6-4670-8f2b-e8d797aa6e9f",
            "on_success": {
                "metadata": {
                    "accounts": [
                        {
                            "class_type": null,
                            "id": "d1rVJdbe1jCE9jE81wlduPlQQejKXeCJnDzyw",
                            "mask": "3333",
                            "name": "Plaid Credit Card",
                            "subtype": "credit card",
                            "type": "credit",
                            "verification_status": null
                        }
                    ],
                    "institution": {
                        "institution_id": "ins_10",
                        "name": "American Express"
                    },
                    "link_session_id": "f3c3b8fe-16c6-4670-8f2b-e8d797aa6e9f",
                    "transfer_status": null
                },
                "public_token": "public-sandbox-d1e64436-f033-4302-a469-b90656edf8c7"
            },
            "results": {
                "item_add_results": [
                    {
                        "accounts": [
                            {
                                "class_type": null,
                                "id": "d1rVJdbe1jCE9jE81wlduPlQQejKXeCJnDzyw",
                                "mask": "3333",
                                "name": "Plaid Credit Card",
                                "subtype": "credit card",
                                "type": "credit",
                                "verification_status": null
                            }
                        ],
                        "institution": {
                            "institution_id": "ins_10",
                            "name": "American Express"
                        },
                        "public_token": "public-sandbox-d1e64436-f033-4302-a469-b90656edf8c7"
                    }
                ]
            },
            "started_at": "2024-07-29T21:22:17.0951134Z"
        }
    ],
    "link_token": "link-sandbox-9b48eb4c-7e1b-44e1-b80d-faa1d71cdf5e",
    "metadata": {
        "client_name": "Wonderwallet",
        "country_codes": [
            "US"
        ],
        "initial_products": [
            "auth"
        ],
        "language": "en",
        "redirect_uri": "{{UNIVERSAL_OR_APP_LINK}}",
        "webhook": "https://wonderwallet.com/webhook_receiver"
    },
    "request_id": "jBBxxNC842EJNqW"
}
`}</code></pre>
    <h4 {...{
      "id": "link-session-events"
    }}>{`Link session events`}</h4>
    <p>{`To obtain Link session events while using Hosted Link, listen for the `}<a parentName="p" {...{
        "href": "/docs/api/link/#events"
      }}><inlineCode parentName="a">{`EVENTS`}</inlineCode></a>{` webhook, or use `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "integrating-hosted-link-in-native-mobile-applications"
    }}>{`Integrating Hosted Link in native mobile applications`}</h4>
    <p>{`If you are unable to use the Plaid Link SDK in your native mobile app, creating and opening up a Hosted Link is a stable and reliable way of taking users through the Link process. We recommend using a Hosted Link instead of trying to use the Plaid Web SDK in a webview. `}</p>
    <p>{`When you use a Hosted Link in a native mobile app, you will open the URL inside of an "out of process" webview, such as an `}<inlineCode parentName="p">{`ASWebAuthenticationSession`}</inlineCode>{` in iOS, or an Android Custom Tab. These sessions appear as part of your application, making the process feel more seamless than opening up the URL in a separate browser application. `}</p>
    <p>{`When the Link session is complete, Plaid will attempt to open a `}<inlineCode parentName="p">{`completion_redirect_uri`}</inlineCode>{` that you specify. This is typically a URI with a custom scheme that automatically closes the web session and allows your application to continue from where you left off.`}</p>
    <p>{`To use Hosted Link in a native mobile application:`}</p>
    <h5 {...{
      "id": "create-a-link-token"
    }}>{`Create a Link token`}</h5>
    <p>{`Call `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` with the following values:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`hosted_link.is_mobile_app`}</inlineCode>{` should be set to `}<inlineCode parentName="li">{`true`}</inlineCode></li>
      <li parentName="ul"><inlineCode parentName="li">{`hosted_link.completion_redirect_uri`}</inlineCode>{` should be a URI with a custom scheme. `}
        <ul parentName="li">
          <li parentName="ul">{`This scheme can be any value you'd like, as long as it isn't a reserved one such as `}<inlineCode parentName="li">{`http`}</inlineCode>{` or `}<inlineCode parentName="li">{`tel`}</inlineCode>{`. By convention, it's often one associated with the name of your application.`}</li>
          <li parentName="ul">{`For example, your app might have something like `}<inlineCode parentName="li">{`wonderwallet://hosted-link-complete`}</inlineCode>{` as a completion redirect URI. `}</li>
          <li parentName="ul">{`Unlike the `}<inlineCode parentName="li">{`redirect_uri`}</inlineCode>{` value, this URI does not need to be registered with the Plaid Dashboard, nor does it need to point to a "real" location.`}</li>
        </ul>
      </li>
      <li parentName="ul"><inlineCode parentName="li">{`redirect_uri`}</inlineCode>{` is required when `}<inlineCode parentName="li">{`hosted_link.is_mobile_app`}</inlineCode>{` is `}<inlineCode parentName="li">{`true`}</inlineCode>{`, and should be a URL that opens up your native mobile application, such as a Universal Link on iOS or an Android App Link on Android. `}
        <ul parentName="li">
          <li parentName="ul">{`The `}<inlineCode parentName="li">{`redirect_uri`}</inlineCode>{` is used in situations where Plaid opens up another mobile app, like Chase, for app-to-app authentication. `}</li>
          <li parentName="ul">{`When the user is done authenticating with the third party app, the app will attempt to open this URI, which should redirect users back into your app.`}</li>
          <li parentName="ul">{`Plaid cannot use `}<inlineCode parentName="li">{`android_package_name`}</inlineCode>{` to perform the OAuth redirect when using Hosted Link. You must use `}<inlineCode parentName="li">{`redirect_uri`}</inlineCode>{`, even if using an Android app.`}</li>
          <li parentName="ul">{`There is nothing you need to do to handle this incoming `}<inlineCode parentName="li">{`redirect_uri`}</inlineCode>{`. As long as the link opens your application, the Hosted Link session can take it from there.`}</li>
        </ul>
      </li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Sample /link/token/create request for mobile apps",
        "switcher": "false",
        "#Sample": true,
        "/link/token/create": true,
        "request": true,
        "for": true,
        "mobile": true,
        "apps": true
      }}>{`
curl -X POST https://production.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
     ...Other data removed...
      "hosted_link": {
        "completion_redirect_uri": "wonderwallet://hosted-link-complete",
        "is_mobile_app": true
      },
      "redirect_uri": "https://mysite.com/universal-link/jump-to-my-app.html"
}'
`}</code></pre>
    <h5 {...{
      "id": "register-your-custom-url-scheme"
    }}>{`"Register" your custom URL scheme`}</h5>
    <p>{`You will need to make sure your operating system can handle the completion redirect URI that the Hosted Link session will open when it's complete. `}</p>
    <p>{`On iOS, all you need to do is pass this URL scheme into the `}<inlineCode parentName="p">{`ASWebAuthenticationSession`}</inlineCode>{` when you initialize it (see `}<a parentName="p" {...{
        "href": "#open-the-hosted-link-url"
      }}>{`Open the Hosted Link URL`}</a>{`). There is no need to register this URL scheme in your Xcode project in any other way.`}</p>
    <p>{`In Android, you should create an Intent Filter for the activity that you want to appear when the user is done with the session.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "switcher=false #Sample Android Manifest snippet",
        "switcher": "false",
        "#Sample": true,
        "Android": true,
        "Manifest": true,
        "snippet": true
      }}>{`<intent-filter>
  <action android:name="android.intent.action.VIEW" />
  <category android:name="android.intent.category.DEFAULT" />
  <category android:name="android.intent.category.BROWSABLE" />
  <data android:scheme="wonderwallet" android:host="hosted-link-complete" />
</intent-filter>
`}</code></pre>
    <p>{`If this activity is the same activity that you used to launch Hosted Link in the first place, you may want to give this activity a `}<inlineCode parentName="p">{`singleTask`}</inlineCode>{` or `}<inlineCode parentName="p">{`singleTop`}</inlineCode>{` launch mode, so that the original activity "pops back" to the top of your application, and you're not creating multiple copies of the same activity.`}</p>
    <h5 {...{
      "id": "open-the-hosted-link-url"
    }}>{`Open the Hosted Link URL`}</h5>
    <p>{`On iOS, create an `}<inlineCode parentName="p">{`ASWebAuthenticationSession`}</inlineCode>{`, using the Hosted Link URL you received from the server and the same custom scheme you used in your `}<inlineCode parentName="p">{`completion_redirect_uri`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-swift",
        "metastring": "switcher=false #Sample call starting ASWebAuthenticationSession in Swift",
        "switcher": "false",
        "#Sample": true,
        "call": true,
        "starting": true,
        "ASWebAuthenticationSession": true,
        "in": true,
        "Swift": true
      }}>{`@IBAction func connectBankWasPressed(_ sender: Any) {
    guard let hostedLinkURL = hostedLinkURL else {return}
    let scheme = "wonderwallet"
    let session = ASWebAuthenticationSession(url: URL(string: hostedLinkURL)!, callbackURLScheme: scheme) { callbackURL, error in
        guard error == nil, let callbackURL = callbackURL else {
            // The user might have clicked "Cancel" on the session. This will be captured as a 'canceledLogin' error.
            return
        }
        if (callbackURL.absoluteString == "\\(scheme)://hosted-link-complete") {
            self.checkLinkStatus()
        }
    }
    session.presentationContextProvider = self
    session.start()
}
`}</code></pre>
    <p>{`For the above code to work, you will also need to make sure the appropriate view controller is set as a proper context provider:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-swift",
        "metastring": "switcher=false #Setting a context provider in UIKit",
        "switcher": "false",
        "#Setting": true,
        "a": true,
        "context": true,
        "provider": true,
        "in": true,
        "UIKit": true
      }}>{`extension ConnectToBankViewController: ASWebAuthenticationPresentationContextProviding {
    func presentationAnchor(for session: ASWebAuthenticationSession) -> ASPresentationAnchor {
        return view.window!
    }
}
`}</code></pre>
    <p>{`On Android devices, create a Custom Tab that opens up the Hosted Link URL from within your application:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-kotlin",
        "metastring": "switcher=false #Creating a Custom Tab example in Kotlin",
        "switcher": "false",
        "#Creating": true,
        "a": true,
        "Custom": true,
        "Tab": true,
        "example": true,
        "in": true,
        "Kotlin": true
      }}>{`import androidx.browser.customtabs.CustomTabsIntent
import android.net.Uri

private fun openHostedLink(hostedLinkUrl: String) {
  val customTabsIntent = CustomTabsIntent.Builder().build()
  customTabsIntent.launchUrl(this, Uri.parse(hostedLinkUrl))
}
`}</code></pre>
    <p>{`See the `}<a parentName="p" {...{
        "href": "https://developer.apple.com/documentation/authenticationservices/aswebauthenticationsession"
      }}>{`Apple Developer Documentation`}</a>{` or `}<a parentName="p" {...{
        "href": "https://developer.chrome.com/docs/android/custom-tabs"
      }}>{`Chrome Developer Documentation`}</a>{` for more details on these two methods.`}</p>
    <h5 {...{
      "id": "handle-the-incoming-uri"
    }}>{`Handle the incoming URI`}</h5>
    <p>{`When the user is done with the Hosted Link session, Plaid will attempt to open the `}<inlineCode parentName="p">{`completion_redirect_uri`}</inlineCode>{` you specified when creating the Link token. If your application is set up as described above, this will close the Hosted Link page and bring your application back to the foreground. You will then receive this URI in a callback handler, where you can perform tasks such as refreshing the user's Link Session status.`}</p>
    <Callout mdxType="Callout">
      <p>{`The `}<inlineCode parentName="p">{`completion_redirect_uri`}</inlineCode>{` is not supported in Identity Verification sessions.`}</p>
    </Callout>
    <p>{`Note that Plaid will call this URI whether the user successfully completed the session, or asked Plaid to exit the connection process, so you cannot assume that receiving this URI means a successful connection. You should listen for the `}<a parentName="p" {...{
        "href": "/docs/api/link/#session_finished"
      }}><inlineCode parentName="a">{`SESSION_FINISHED`}</inlineCode></a>{` webhook or call `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` to determine the status of the user's Hosted Link session.`}</p>
    <p>{`On iOS, you define this handler when you create the `}<inlineCode parentName="p">{`ASWebAuthenticationSession`}</inlineCode></p>
    <pre><code parentName="pre" {...{
        "className": "language-swift",
        "metastring": "switcher=false #Sample callback handler in ASWebAuthenticationSession",
        "switcher": "false",
        "#Sample": true,
        "callback": true,
        "handler": true,
        "in": true,
        "ASWebAuthenticationSession": true
      }}>{`let scheme = "wonderwallet"
let session = ASWebAuthenticationSession(url: URL(string: hostedLinkURL)!, callbackURLScheme: scheme) { callbackURL, error in
    guard error == nil, let callbackURL = callbackURL else {
        return
    }
    if (callbackURL.absoluteString == "\\(scheme)://hosted-link-complete") {
        self.checkLinkStatus()
    }
}
`}</code></pre>
    <p>{`If the user dismisses the window by clicking the Cancel button, this handler will still be called, but with a `}<inlineCode parentName="p">{`canceledLogin`}</inlineCode>{` error instead of a `}<inlineCode parentName="p">{`callbackURL`}</inlineCode>{`.`}</p>
    <p>{`On Android, you can retrieve this URL in the `}<inlineCode parentName="p">{`onNewIntent`}</inlineCode>{` callback if you are returning to the previous activity. (If you are opening a new activity, you can use the `}<inlineCode parentName="p">{`onCreate`}</inlineCode>{` callback.)`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-kotlin",
        "metastring": "switcher=false #Sample capturing the incoming URI in onNewIntent",
        "switcher": "false",
        "#Sample": true,
        "capturing": true,
        "the": true,
        "incoming": true,
        "URI": true,
        "in": true,
        "onNewIntent": true
      }}>{`  override fun onNewIntent(intent: Intent?) {
    super.onNewIntent(intent)
    intent?.data?.let { uri ->
      if (uri.scheme == "wonderwallet" && uri.host == "hosted-link-complete") {
        checkLinkStatus()
      }
    }
  }
`}</code></pre>
    <p>{`If the user dismisses the Custom Tab by tapping the close button, this callback will not be called, but an `}<inlineCode parentName="p">{`onResume`}</inlineCode>{` callback will still be called.`}</p>
    <h4 {...{
      "id": "sample-code"
    }}>{`Sample code`}</h4>
    <p><a parentName="p" {...{
        "href": "https://github.com/plaid/tiny-quickstart/tree/main/hosted_link"
      }}>{`Tiny Quickstart: Hosted Link`}</a>{` contains a minimal Plaid integration showing the Hosted Link flow.`}</p>
    <h4 {...{
      "id": "migrating-to-hosted-link-from-other-integrations"
    }}>{`Migrating to Hosted Link from other integrations`}</h4>
    <p>{`First, review the `}<a parentName="p" {...{
        "href": "#integration-process"
      }}>{`Integration process`}</a>{` to understand how to launch the Hosted Link flow and the configuration options available.`}</p>
    <p>{`If you're currently capturing Link session events, migrate to using `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{` to receive Link session events.`}</p>
    <p>{`If your users will be opening Link sessions inside a native mobile application, launch Hosted Link in an out-of-process webview as described above instead of using an in-process webview. `}</p>
    <h5 {...{
      "id": "iframe-based-nested-integrations"
    }}>{`iFrame based nested integrations`}</h5>
    <p>{`For nested integrations like iFrames in a customer web page within a merchant webview, shift from capturing events through redirects to using backend events via `}<inlineCode parentName="p">{`/link/token/get`}</inlineCode>{`. Your merchants should launch Hosted Link in an out-of-process webview or external browser. `}</p>
    <p>{`Either you or the merchant must add an extra callback parameter to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, allowing Plaid to close the out-of-process webview as needed. You will likely make the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call. Merchants must either obtain a callback from the customer or ensure handling of a specified callback in their code.`}</p>
    <h5 {...{
      "id": "next-steps"
    }}>{`Next steps`}</h5>
    <p>{`Once you have obtained a public token, you can integrate via the same process as non-Hosted Link integrations, including calling `}<inlineCode parentName="p">{`/item/public_token/exchange`}</inlineCode>{` to exchange the public token for an access token. `}</p>
    <h4 {...{
      "id": "testing-hosted-link"
    }}>{`Testing Hosted Link`}</h4>
    <p>{`You can test Hosted Link in Production or Sandbox. In the Sandbox environment, Plaid will not provide email or SMS Hosted Link delivery, and update mode sessions will expire after 30 minutes.`}</p>
    <h4 {...{
      "id": "pricing"
    }}>{`Pricing`}</h4>
    <p>{`When using Link Delivery to deliver Hosted Link sessions, there is a fee for each link sent. For Link Delivery pricing details, `}<a parentName="p" {...{
        "href": "https://plaid.com/contact/"
      }}>{`contact sales`}</a>{` or your account manager.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "integration-process",
        level: 4,
        title: "Integration process",
      },
{
        id: "link-session-events",
        level: 4,
        title: "Link session events",
      },
{
        id: "integrating-hosted-link-in-native-mobile-applications",
        level: 4,
        title: "Integrating Hosted Link in native mobile applications",
      },
{
        id: "sample-code",
        level: 4,
        title: "Sample code",
      },
{
        id: "migrating-to-hosted-link-from-other-integrations",
        level: 4,
        title: "Migrating to Hosted Link from other integrations",
      },
{
        id: "testing-hosted-link",
        level: 4,
        title: "Testing Hosted Link",
      },
{
        id: "pricing",
        level: 4,
        title: "Pricing",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
